import React, { useState, useEffect } from 'react';

/**
 * Composant Modale Copilote - Remplissage assisté de déclarations TVA & URSSAF
 */
const CopiloteDeclaration = ({ clientId, onClose, type, data }) => {
  const [step, setStep] = useState(1); // 1: Récap, 2: Connexion & Remplissage, 3: Validation utilisateur
  const [status, setStatus] = useState({
    statut: 'INACTIF',
    etape: 0,
    message: 'Prêt à démarrer',
    type: null
  });
  const [loading, setLoading] = useState(false);
  const [serviceAvailable, setServiceAvailable] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  // Vérification de la disponibilité du service au montage
  useEffect(() => {
    const checkService = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch('/copilote/sante', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const resData = await res.json();
        if (!resData.success) {
          setServiceAvailable(false);
          setErrorMsg(resData.message);
        }
      } catch (err) {
        setServiceAvailable(false);
        setErrorMsg("Le microservice de copilotage n'est pas joignable sur le port 3001.");
      }
    };
    checkService();
  }, []);

  // Gestion du Polling pour suivre l'état de Playwright
  useEffect(() => {
    let intervalId = null;

    if (step === 2) {
      intervalId = setInterval(async () => {
        try {
          const token = localStorage.getItem('auth_token');
          const res = await fetch('/copilote/statut', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const statusData = await res.json();
          
          setStatus(statusData);

          // Si Playwright passe en état d'attente de validation finale
          if (statusData.statut === 'ATTENTE_VALIDATION') {
            setStep(3);
            clearInterval(intervalId);
          } else if (statusData.statut === 'ERREUR') {
            setErrorMsg(statusData.message);
            setStep(1);
            clearInterval(intervalId);
          } else if (statusData.statut === 'INACTIF' && status.statut !== 'DEMARRAGE') {
            // Si le navigateur a été fermé
            setErrorMsg("Le navigateur a été fermé par l'utilisateur.");
            setStep(1);
            clearInterval(intervalId);
          }
        } catch (err) {
          console.error("Erreur polling statut copilote:", err);
        }
      }, 2000); // toutes les 2 secondes
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [step, status.statut]);

  // Lance la session copilote
  const handleStartCopilote = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const token = localStorage.getItem('auth_token');
      let endpoint = '';
      let bodyData = {};

      if (type === 'TVA') {
        endpoint = '/copilote/tva/demarrer';
        bodyData = { lignes_ca3: data.lignes_ca3 || data };
      } else if (type === 'URSSAF') {
        endpoint = '/copilote/urssaf/demarrer';
        bodyData = { montant_total: Number(data.montant_total || data.montant), periode: data.periode };
      } else if (type === 'IS') {
        endpoint = '/copilote/is/demarrer';
        const isData = data.is_data || data;
        bodyData = {
          resultat_comptable: Number(isData.resultat_comptable || 0),
          is_estime: Number(isData.is_estime || 0),
          resultat_net_is: Number(isData.resultat_net_is || 0)
        };
      } else if (type === 'BILAN') {
        endpoint = '/copilote/bilan/demarrer';
        const bilanData = data.bilan_data || data;
        bodyData = {
          total_actif: Number(bilanData.total_actif || 0),
          produits_total: Number(bilanData.produits_total || 0),
          charges_total: Number(bilanData.charges_total || 0),
          resultat_net: Number(bilanData.resultat_net || 0)
        };
      }

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bodyData)
      });

      const resData = await res.json();
      
      if (res.ok && resData.success) {
        setStep(2);
        setStatus(resData.status || { statut: 'DEMARRAGE', etape: 1, message: 'Lancement du navigateur...' });
      } else {
        setErrorMsg(resData.detail || resData.message || "Erreur au démarrage du navigateur.");
      }
    } catch (err) {
      setErrorMsg("Impossible de démarrer la session. Vérifiez que le microservice Node.js est actif.");
    } finally {
      setLoading(false);
    }
  };

  // Ferme la session proprement
  const handleStopCopilote = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch('/copilote/session', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Erreur fermeture session:", err);
    } finally {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900 bg-opacity-70 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col transform transition-all duration-300">
        
        {/* En-tête */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-5 text-white flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">🤖</span>
            <div>
              <h3 className="font-bold text-lg tracking-wide">Copilote de Déclaration</h3>
              <p className="text-xs text-emerald-100 font-medium">Automatisation intelligente et assistée</p>
            </div>
          </div>
          <button 
            onClick={handleStopCopilote} 
            className="text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Corps */}
        <div className="p-6 flex-1 overflow-y-auto">
          {!serviceAvailable ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">⚠️</div>
              <h4 className="font-semibold text-slate-800 mb-2">Service indisponible</h4>
              <p className="text-sm text-slate-500 mb-4 px-2">{errorMsg}</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 text-left mb-6">
                💡 <strong>Comment résoudre cela ?</strong><br />
                Lancez le microservice en ouvrant un terminal dans le dossier <code>playwright-service</code> et tapez la commande :<br />
                <code className="bg-white border border-amber-300 px-1 py-0.5 rounded block mt-1.5 text-center select-all font-mono font-semibold">npm start</code>
              </div>
              <button 
                onClick={onClose}
                className="w-full bg-slate-800 text-white font-semibold py-2.5 rounded-xl hover:bg-slate-700 transition-colors"
              >
                Fermer et déclarer manuellement
              </button>
            </div>
          ) : (
            <>
              {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 mb-4 text-sm flex items-start space-x-2">
                  <span className="text-lg">❌</span>
                  <p className="flex-1">{errorMsg}</p>
                </div>
              )}

              {/* Étape 1 : Récapitulatif */}
              {step === 1 && (
                <div className="space-y-4">
                  <div className="border border-slate-100 rounded-xl p-4 bg-slate-50">
                    <h4 className="font-semibold text-sm text-slate-500 uppercase tracking-wider mb-3">Données à déclarer</h4>
                    
                    {type === 'TVA' && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Base imposable (20%) :</span>
                          <span className="font-bold text-slate-800">{Math.round(data.lignes_ca3?.["01_operations_imposables_20"] || 0)} €</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">TVA collectée brute (20%) :</span>
                          <span className="font-bold text-slate-800">{Math.round(data.lignes_ca3?.["08_tva_20"] || 0)} €</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">TVA déductible (Autres biens) :</span>
                          <span className="font-bold text-slate-800">{Math.round(data.lignes_ca3?.["20_tva_deductible_autres"] || 0)} €</span>
                        </div>
                        <hr className="my-2 border-slate-200" />
                        <div className="flex justify-between text-base font-bold">
                          <span className="text-emerald-700">Solde TVA nette :</span>
                          <span className="text-emerald-700">{Math.round(Math.abs(data.solde?.montant || data.lignes_ca3?.["28_tva_nette"] || 0))} € ({data.solde?.nature === 'credit_tva' ? 'Crédit' : 'À payer'})</span>
                        </div>
                      </div>
                    )}
                    
                    {type === 'URSSAF' && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Période :</span>
                          <span className="font-bold text-slate-800">{data.periode}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Type de Déclaration :</span>
                          <span className="font-bold text-slate-800">Cotisations URSSAF</span>
                        </div>
                        <hr className="my-2 border-slate-200" />
                        <div className="flex justify-between text-base font-bold">
                          <span className="text-emerald-700">Montant total à déclarer :</span>
                          <span className="text-emerald-700">{Number(data.montant_total || data.montant).toFixed(2)} €</span>
                        </div>
                      </div>
                    )}

                    {type === 'IS' && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Résultat fiscal imposable :</span>
                          <span className="font-bold text-slate-800">{Math.round(data.is_data?.resultat_comptable || data.resultat_comptable || 0)} €</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Taux standard IS (25%) :</span>
                          <span className="font-bold text-slate-800">25.0 %</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Montant IS estimé :</span>
                          <span className="font-bold text-slate-800">{Math.round(data.is_data?.is_estime || data.is_estime || 0)} €</span>
                        </div>
                        <hr className="my-2 border-slate-200" />
                        <div className="flex justify-between text-base font-bold">
                          <span className="text-emerald-700">Résultat net après IS :</span>
                          <span className="text-emerald-700">{Math.round(data.is_data?.resultat_net_is || data.resultat_net_is || 0)} €</span>
                        </div>
                      </div>
                    )}

                    {type === 'BILAN' && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Total Actif du Bilan :</span>
                          <span className="font-bold text-slate-800">{Math.round(data.bilan_data?.total_actif || data.total_actif || 0)} €</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Chiffre d'Affaires (Produits) :</span>
                          <span className="font-bold text-slate-800">{Math.round(data.bilan_data?.produits_total || data.produits_total || 0)} €</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-600">Charges Totales :</span>
                          <span className="font-bold text-slate-800">{Math.round(data.bilan_data?.charges_total || data.charges_total || 0)} €</span>
                        </div>
                        <hr className="my-2 border-slate-200" />
                        <div className="flex justify-between text-base font-bold">
                          <span className="text-emerald-700">Résultat Net Comptable :</span>
                          <span className="text-emerald-700">{Math.round(data.bilan_data?.resultat_net || data.resultat_net || 0)} €</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-xl p-3.5 leading-relaxed space-y-1">
                    <p className="font-bold">⚠️ Comment fonctionne le Copilote ?</p>
                    <ol className="list-decimal pl-4 space-y-1">
                      <li>Le Copilote va ouvrir une fenêtre de navigateur Chromium <strong>visible</strong>.</li>
                      <li>Vous devrez vous <strong>connecter vous-même</strong> (identifiants, SMS, etc.).</li>
                      <li>Dès que vous serez connecté, le Copilote <strong>saisira automatiquement</strong> les montants ci-dessus.</li>
                      <li>Vous ferez la vérification finale et cliquerez sur <strong>valider</strong>.</li>
                    </ol>
                  </div>

                  <button
                    onClick={handleStartCopilote}
                    disabled={loading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Démarrage en cours...</span>
                      </>
                    ) : (
                      <>
                        <span>🚀 Lancer le Copilote</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Étape 2 : Connexion de l'utilisateur */}
              {step === 2 && (
                <div className="text-center py-6 space-y-4">
                  <div className="relative w-20 h-20 mx-auto flex items-center justify-center">
                    <div className="absolute inset-0 rounded-full border-4 border-emerald-100 animate-pulse"></div>
                    <div className="absolute inset-0 rounded-full border-t-4 border-emerald-600 animate-spin"></div>
                    <span className="text-3xl animate-bounce">🌐</span>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-bold text-slate-800 text-lg">
                      {status.statut === 'EN_ATTENTE_CONNEXION' ? '🔑 Connexion requise' : '🤖 Action en cours'}
                    </h4>
                    <p className="text-sm text-slate-500 font-medium max-w-sm mx-auto">
                      {status.message}
                    </p>
                  </div>

                  {status.statut === 'EN_ATTENTE_CONNEXION' && (
                    <div className="bg-sky-50 border border-sky-100 text-sky-800 text-xs rounded-xl p-4 text-left shadow-sm">
                      <p className="font-bold mb-1.5 flex items-center">
                        <span className="mr-1.5">👉</span> À faire dans le navigateur ouvert :
                      </p>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>Identifiez-vous sur le site officiel (Impôts, URSSAF ou Guichet Unique).</li>
                        <li>Passez la double authentification si demandée (SMS/Certificat).</li>
                        <li>Laissez l'assistant surveiller l'accès. Il prendra le relais automatiquement.</li>
                      </ul>
                    </div>
                  )}

                  <div className="pt-2 text-xs font-semibold text-slate-400">
                    Progression : Étape {status.etape} / 6
                  </div>
                </div>
              )}

              {/* Étape 3 : Rempli, attente de validation */}
              {step === 3 && (
                <div className="space-y-6 text-center py-4">
                  <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-3xl shadow-inner">
                    🎉
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-bold text-slate-800 text-xl">Remplissage effectué !</h4>
                    <p className="text-sm text-slate-500 px-4">
                      Le copilote a fini d'injecter tous les montants calculés dans les bonnes cases du formulaire officiel.
                    </p>
                  </div>

                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-left text-xs text-emerald-800 space-y-2 leading-relaxed">
                    <p className="font-bold flex items-center">
                      <span className="text-base mr-1.5">🔔</span> Action requise :
                    </p>
                    <ol className="list-decimal pl-4 space-y-1">
                      <li>Vérifiez attentivement les cases du formulaire affiché dans le navigateur Chromium.</li>
                      <li>Renseignez les éventuels autres détails demandés.</li>
                      <li>Cliquez sur le bouton <strong>"Valider"</strong> ou <strong>"Soumettre"</strong> pour finaliser officiellement votre déclaration.</li>
                    </ol>
                  </div>

                  <button
                    onClick={handleStopCopilote}
                    className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-xl transition-all shadow-md"
                  >
                    Fermer le Copilote (Fermer le navigateur)
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopiloteDeclaration;
